import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Terminal, Package, Settings, Rocket, Code, DollarSign } from 'lucide-react';

const Setup = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen">
      <div className="circuit-bg" />
      <div className="circuit-overlay" />
      
      <div className="content-wrapper">
        {/* Navigation */}
        <nav className="border-b border-green-500/20 backdrop-blur-md" data-testid="setup-navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-green-400 hover:text-green-300"
                data-testid="back-button"
              >
                <ArrowLeft className="mr-2 w-5 h-5" />
                Back to Home
              </Button>
            </div>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h1 className="text-5xl md:text-6xl font-bold text-center mb-8 glow-text" data-testid="page-title">
            SETUP GUIDE
          </h1>
          
          <p className="text-xl text-green-300 text-center mb-16 leading-relaxed" data-testid="page-subtitle">
            Follow these steps to deploy your own instance of Millionaire The Machine.
          </p>

          {/* Prerequisites */}
          <section className="mb-12" data-testid="prerequisites-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 flex items-center">
              <Package className="w-8 h-8 mr-3" />
              Prerequisites
            </h2>
            <Card className="card-tech p-8">
              <ul className="space-y-3 text-green-300 text-lg">
                <li className="flex items-start" data-testid="prereq-0">
                  <span className="text-green-500 mr-3">•</span>
                  <span>PayPal Business Account (for receiving payments)</span>
                </li>
                <li className="flex items-start" data-testid="prereq-1">
                  <span className="text-green-500 mr-3">•</span>
                  <span>GitHub account (for hosting the repository)</span>
                </li>
                <li className="flex items-start" data-testid="prereq-2">
                  <span className="text-green-500 mr-3">•</span>
                  <span>Node.js and Python installed (for local development)</span>
                </li>
                <li className="flex items-start" data-testid="prereq-3">
                  <span className="text-green-500 mr-3">•</span>
                  <span>Hosting platform account (Vercel, Netlify, or similar)</span>
                </li>
              </ul>
            </Card>
          </section>

          {/* Step 1: PayPal Setup */}
          <section className="mb-12" data-testid="paypal-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 flex items-center">
              <DollarSign className="w-8 h-8 mr-3" />
              Step 1: PayPal Setup
            </h2>
            <Card className="card-tech p-8">
              <div className="space-y-4 text-green-300 text-lg">
                <p className="font-semibold text-green-400">Create PayPal Business Account:</p>
                <ol className="space-y-3 ml-6">
                  <li data-testid="paypal-step-1">1. Go to <a href="https://www.paypal.com/business" target="_blank" rel="noopener noreferrer" className="text-green-400 underline">paypal.com/business</a></li>
                  <li data-testid="paypal-step-2">2. Sign up for a Business account</li>
                  <li data-testid="paypal-step-3">3. Navigate to Dashboard → Settings → Account Settings</li>
                  <li data-testid="paypal-step-4">4. Go to "Website Payments" → "API Access"</li>
                  <li data-testid="paypal-step-5">5. Click "Manage API Credentials"</li>
                  <li data-testid="paypal-step-6">6. Select "REST API apps" and click "Create App"</li>
                  <li data-testid="paypal-step-7">7. Copy your Client ID and Secret</li>
                </ol>
                
                <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded">
                  <p className="text-yellow-400 font-semibold">Important:</p>
                  <p className="text-green-300 mt-2">
                    Start with Sandbox (test) mode. Once you're ready for real payments, 
                    switch to Live mode in your .env configuration.
                  </p>
                </div>
              </div>
            </Card>
          </section>

          {/* Step 2: Clone Repository */}
          <section className="mb-12" data-testid="clone-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 flex items-center">
              <Code className="w-8 h-8 mr-3" />
              Step 2: Clone & Install
            </h2>
            <Card className="card-tech p-8">
              <p className="text-green-300 text-lg mb-4">Clone the repository and install dependencies:</p>
              
              <div className="space-y-4">
                <div className="terminal" data-testid="terminal-clone">
                  <div className="text-green-400 mb-2"># Clone the repository</div>
                  <code>git clone https://github.com/yourusername/millionaire-machine.git</code>
                </div>
                
                <div className="terminal" data-testid="terminal-backend">
                  <div className="text-green-400 mb-2"># Install backend dependencies</div>
                  <code>cd millionaire-machine/backend</code><br />
                  <code>pip install -r requirements.txt</code>
                </div>
                
                <div className="terminal" data-testid="terminal-frontend">
                  <div className="text-green-400 mb-2"># Install frontend dependencies</div>
                  <code>cd ../frontend</code><br />
                  <code>yarn install</code>
                </div>
              </div>
            </Card>
          </section>

          {/* Step 3: Configuration */}
          <section className="mb-12" data-testid="config-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 flex items-center">
              <Settings className="w-8 h-8 mr-3" />
              Step 3: Configure Environment
            </h2>
            <Card className="card-tech p-8">
              <p className="text-green-300 text-lg mb-4">Create a .env file in the backend directory:</p>
              
              <div className="code-block" data-testid="env-example">
                <code>
                  MONGO_URL="mongodb://localhost:27017"<br />
                  DB_NAME="millionaire_machine"<br />
                  CORS_ORIGINS="*"<br />
                  <br />
                  # PayPal Configuration<br />
                  PAYPAL_MODE="sandbox"  # Change to "live" for production<br />
                  PAYPAL_CLIENT_ID="your_client_id_here"<br />
                  PAYPAL_CLIENT_SECRET="your_secret_here"
                </code>
              </div>
              
              <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded">
                <p className="text-red-400 font-semibold">Security Warning:</p>
                <p className="text-green-300 mt-2">
                  Never commit your .env file to Git! Add it to .gitignore. 
                  Keep your API keys secret and secure.
                </p>
              </div>
            </Card>
          </section>

          {/* Step 4: Local Testing */}
          <section className="mb-12" data-testid="testing-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 flex items-center">
              <Terminal className="w-8 h-8 mr-3" />
              Step 4: Test Locally
            </h2>
            <Card className="card-tech p-8">
              <p className="text-green-300 text-lg mb-4">Run the application locally to test:</p>
              
              <div className="space-y-4">
                <div className="terminal" data-testid="terminal-run-backend">
                  <div className="text-green-400 mb-2"># Start backend (in backend directory)</div>
                  <code>uvicorn server:app --reload --port 8001</code>
                </div>
                
                <div className="terminal" data-testid="terminal-run-frontend">
                  <div className="text-green-400 mb-2"># Start frontend (in frontend directory)</div>
                  <code>yarn start</code>
                </div>
              </div>
              
              <p className="text-green-300 text-lg mt-6">
                Visit <span className="text-green-400 font-mono">http://localhost:3000</span> to see your instance running!
              </p>
            </Card>
          </section>

          {/* Step 5: Deploy */}
          <section className="mb-12" data-testid="deploy-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 flex items-center">
              <Rocket className="w-8 h-8 mr-3" />
              Step 5: Deploy to Production
            </h2>
            <Card className="card-tech p-8">
              <p className="text-green-300 text-lg mb-6">Deploy to your preferred hosting platform:</p>
              
              <div className="space-y-6">
                <div data-testid="deploy-vercel">
                  <h3 className="text-xl font-bold text-green-400 mb-3">Option 1: Vercel (Recommended)</h3>
                  <div className="terminal">
                    <code>npm i -g vercel</code><br />
                    <code>vercel deploy</code>
                  </div>
                </div>
                
                <div data-testid="deploy-netlify">
                  <h3 className="text-xl font-bold text-green-400 mb-3">Option 2: Netlify</h3>
                  <div className="terminal">
                    <code>npm i -g netlify-cli</code><br />
                    <code>netlify deploy</code>
                  </div>
                </div>
                
                <div data-testid="deploy-github">
                  <h3 className="text-xl font-bold text-green-400 mb-3">Option 3: GitHub Pages</h3>
                  <p className="text-green-300 mb-2">Enable GitHub Pages in repository settings and push to gh-pages branch.</p>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded">
                <p className="text-blue-400 font-semibold">Pro Tip:</p>
                <p className="text-green-300 mt-2">
                  Set your environment variables in your hosting platform's dashboard. 
                  Don't forget to switch PAYPAL_MODE to "live" for production!
                </p>
              </div>
            </Card>
          </section>

          {/* Step 6: Share */}
          <section className="mb-12" data-testid="share-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 flex items-center">
              <Rocket className="w-8 h-8 mr-3" />
              Step 6: Share & Earn
            </h2>
            <Card className="card-tech p-8">
              <p className="text-green-300 text-lg mb-4">Now that your instance is live:</p>
              
              <ul className="space-y-3 text-green-300 text-lg ml-6">
                <li data-testid="share-0">• Share on social media (Twitter, Facebook, LinkedIn)</li>
                <li data-testid="share-1">• Email your network</li>
                <li data-testid="share-2">• Post in relevant forums and communities</li>
                <li data-testid="share-3">• Create content (blog posts, videos) explaining the system</li>
                <li data-testid="share-4">• Run ads (Google, Facebook) if you want to scale faster</li>
              </ul>
              
              <p className="text-green-300 text-lg mt-6">
                Every $1 payment goes directly to YOUR PayPal account. Scale it up!
              </p>
            </Card>
          </section>

          {/* CTA */}
          <div className="text-center" data-testid="cta-container">
            <Card className="card-tech p-12 pulse-glow">
              <h2 className="text-4xl font-bold mb-6 glow-text">NEED THE COMPLETE CODE?</h2>
              <p className="text-xl text-green-300 mb-8">
                Get instant access to the full source code, documentation, and setup files for just $1.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="btn-primary text-xl px-12 py-6"
                data-testid="get-code-button"
              >
                Get the Code - $1
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setup;